import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Input } from '../components/ui/input';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import Logo from '../components/Logo';
import { Lock, Mail, ShieldCheck, BarChart3, CalendarClock } from 'lucide-react';

const LoginPage = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [remember, setRemember] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!username || !password) {
      toast.error('Inserisci nome utente e password');
      return;
    }
    setLoading(true);
    const res = await login(username, password);
    setLoading(false);
    if (res.success) {
      toast.success(`Benvenuto, ${res.user.name}`);
      navigate('/panoramica');
    } else {
      toast.error(res.error || 'Credenziali non valide');
    }
  };

  return (
    <div className="min-h-screen bg-white grid lg:grid-cols-2">
      {/* LEFT — Brand panel */}
      <div className="relative hidden lg:flex flex-col justify-between p-12 overflow-hidden bg-[#023F80] text-white">
        {/* Decorative grid pattern */}
        <div
          className="absolute inset-0 opacity-[0.12]"
          style={{
            backgroundImage:
              'linear-gradient(#ffffff 1px, transparent 1px), linear-gradient(90deg, #ffffff 1px, transparent 1px)',
            backgroundSize: '40px 40px',
          }}
        />
        {/* Yellow accent circle with company logo */}
        <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full bg-[#FFCB05] opacity-90" />
        <div className="absolute top-6 right-6 w-48 h-48 rounded-full bg-[#FFCB05] flex items-center justify-center shadow-xl z-10">
          <img
            src="/csc-logo.png"
            alt="Centro Sicurezza Consulenze"
            className="w-40 h-40 object-contain"
            data-testid="login-csc-logo"
          />
        </div>
        <div className="absolute -bottom-32 -left-20 w-80 h-80 rounded-full bg-[#023F80] opacity-80" />
        <div className="absolute top-1/2 left-1/3 w-40 h-40 rounded-full bg-white opacity-[0.04]" />

        <div className="relative z-10">
          <Logo size="lg" variant="light" />
        </div>

        <div className="relative z-10">
          <h1 className="text-4xl font-extrabold leading-tight">
            Compliance HSE,<br />
            <span className="text-[#FFCB05]">semplificata.</span>
          </h1>
          <p className="mt-4 text-white/75 text-base max-w-md leading-relaxed">
            La piattaforma di Centro Sicurezza Consulenze per monitorare scadenze, formazione, visite mediche e adempimenti delle aziende clienti, in un solo posto.
          </p>

          <div className="mt-10 grid grid-cols-3 gap-4 max-w-md">
            <Feature icon={ShieldCheck} label="DVR & DPI" />
            <Feature icon={CalendarClock} label="Scadenze" />
            <Feature icon={BarChart3} label="Report" />
          </div>
        </div>

        <div className="relative z-10 text-xs text-white/40">
          © 2026 Centro Sicurezza Consulenze — SafetyDesk81 v1.0
        </div>
      </div>

      {/* RIGHT — Form */}
      <div className="flex items-center justify-center px-6 py-12 lg:p-16">
        <div className="w-full max-w-md">
          <div className="lg:hidden mb-8 flex justify-center">
            <Logo size="xl" />
          </div>

          <div className="mb-8">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#fef7d4] text-[#8a6608] text-[11px] font-bold tracking-wider mb-4">
              <span className="w-1.5 h-1.5 rounded-full bg-[#FFCB05]" />
              AREA RISERVATA
            </div>
            <h2 className="text-3xl font-extrabold text-[#023F80] tracking-tight">Bentornato</h2>
            <p className="text-sm text-slate-500 mt-2">Accedi al tuo account per gestire scadenze e adempimenti.</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1.5">Nome utente o email</label>
              <div className="relative">
                <Mail size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input
                  type="text"
                  placeholder="nome.utente"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  className="h-12 pl-10 rounded-lg border-slate-200 focus-visible:ring-[#023F80] focus-visible:ring-offset-0"
                  autoComplete="username"
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1.5">Password</label>
              <div className="relative">
                <Lock size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input
                  type="password"
                  placeholder="••••••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="h-12 pl-10 rounded-lg border-slate-200 focus-visible:ring-[#023F80] focus-visible:ring-offset-0"
                  autoComplete="current-password"
                />
              </div>
            </div>

            <div className="flex items-center justify-between pt-1">
              <div className="flex items-center gap-2">
                <Switch
                  id="remember"
                  checked={remember}
                  onCheckedChange={setRemember}
                  className="data-[state=checked]:bg-[#023F80]"
                />
                <label htmlFor="remember" className="text-sm text-slate-600 cursor-pointer">Ricordami</label>
              </div>
              <button
                type="button"
                className="text-sm font-semibold text-[#023F80] hover:text-[#012C5B] transition-colors"
                onClick={() => toast.info('Funzionalità in arrivo')}
              >
                Password dimenticata?
              </button>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full h-12 rounded-lg bg-[#023F80] hover:bg-[#012C5B] active:bg-[#001E42] text-white font-bold text-sm tracking-wider shadow-sm transition-colors disabled:opacity-70"
            >
              {loading ? 'Accesso in corso...' : 'ACCEDI'}
            </button>
          </form>

          <div className="mt-6 text-center text-xs text-slate-400">
            powered by <span className="font-semibold text-slate-600">Centro Sicurezza Consulenze</span>
          </div>
        </div>
      </div>
    </div>
  );
};

const Feature = ({ icon: Icon, label }) => (
  <div className="flex flex-col items-center gap-2 p-3 rounded-xl bg-white/5 border border-white/10">
    <Icon size={20} className="text-[#FFCB05]" />
    <span className="text-[11px] font-semibold text-white/80">{label}</span>
  </div>
);

export default LoginPage;
